import { create } from "zustand";
import type { ProfileRow } from "@andrum/database";
import { getSupabase, isAuthEnforced } from "@/lib/supabase/client";
import { fetchOwnProfile } from "@/features/auth/api/auth-api";

/** Minimal session shape so mobile need not depend on supabase types directly. */
type SessionUser = {
  id: string;
  email?: string | undefined;
};

type SessionLike = {
  user: SessionUser;
};

type AuthStatus = "booting" | "anonymous" | "authenticated" | "demo";

interface AuthStore {
  status: AuthStatus;
  session: SessionLike | null;
  user: SessionUser | null;
  profile: ProfileRow | null;
  /** True after profile fetch finished for current session (success or null). */
  profileHydrated: boolean;
  bootstrapped: boolean;
  bootstrap: () => Promise<void>;
  setSession: (session: SessionLike | null) => Promise<void>;
  refreshProfile: () => Promise<void>;
  setProfile: (profile: ProfileRow | null) => void;
  clear: () => void;
  isOnboardingComplete: () => boolean;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  status: "booting",
  session: null,
  user: null,
  profile: null,
  profileHydrated: false,
  bootstrapped: false,

  bootstrap: async () => {
    if (!isAuthEnforced()) {
      set({
        status: "demo",
        session: null,
        user: null,
        profile: null,
        profileHydrated: true,
        bootstrapped: true,
      });
      return;
    }

    const supabase = getSupabase();
    if (!supabase) {
      set({
        status: "demo",
        profileHydrated: true,
        bootstrapped: true,
      });
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    await get().setSession(
      session
        ? { user: { id: session.user.id, email: session.user.email } }
        : null
    );

    supabase.auth.onAuthStateChange((_event, nextSession) => {
      void get().setSession(
        nextSession
          ? {
              user: {
                id: nextSession.user.id,
                email: nextSession.user.email,
              },
            }
          : null
      );
    });

    set({ bootstrapped: true });
  },

  setSession: async (session) => {
    if (!session) {
      set({
        status: isAuthEnforced() ? "anonymous" : "demo",
        session: null,
        user: null,
        profile: null,
        profileHydrated: true,
      });
      return;
    }

    set({
      status: "authenticated",
      session,
      user: session.user,
      profileHydrated: false,
    });

    const result = await fetchOwnProfile();
    if (result.ok) {
      set({ profile: result.data, profileHydrated: true });
    } else {
      // Keep going with null profile but mark hydrated so we don't loop forever
      set({ profileHydrated: true });
    }
  },

  refreshProfile: async () => {
    const { status } = get();
    if (status !== "authenticated") {
      set({ profileHydrated: true });
      return;
    }
    set({ profileHydrated: false });
    const result = await fetchOwnProfile();
    if (result.ok) {
      set({ profile: result.data, profileHydrated: true });
    } else {
      set({ profileHydrated: true });
    }
  },

  setProfile: (profile) => set({ profile, profileHydrated: true }),

  clear: () =>
    set({
      status: isAuthEnforced() ? "anonymous" : "demo",
      session: null,
      user: null,
      profile: null,
      profileHydrated: true,
    }),

  isOnboardingComplete: () => {
    const { status, profile } = get();
    if (status === "demo") return true;
    return Boolean(profile?.onboarding_completed_at);
  },
}));
